import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, And, WorkflowStepInterface } from '@vendasta/automata';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { SalesOrdersService } from '../../../../data-services/sales-orders.service';
import { BrandingService } from '../../../../data-services/whitelabel.service';
import { CommonDisplayActionService } from '../../common/common-display-action.service';
import { CommonDisplayActivityService } from '../../common/common-display-activity.service';
import {
  OrderOriginLabelMap,
  OrderStatusLabelMap,
} from './order-status-changed-panel/order-status-changed-panel.component';

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example {@link AccountGroupCreatedService}
 */
@Injectable({ providedIn: 'root' })
export class OrderStatusChangedService implements CommonDisplayActionService, CommonDisplayActivityService {
  constructor(
    private readonly translateService: TranslateService,
    protected readonly salesOrdersService: SalesOrdersService,
    protected readonly brandingService: BrandingService,
  ) {}

  getTemplateActionSubtitle(namespace: string, step: WorkflowStepInterface, isWaitUntil?: boolean): Observable<string> {
    return this.getActionSubtitle(namespace, step, isWaitUntil);
  }

  getActionSubtitle(namespace: string, step: WorkflowStepInterface, isWaitUntil?: boolean): Observable<string> {
    const and = And.fromRuleInterface(step.choices.rules[0]);

    const orderStatus = and.value[0]?.value as number;
    const orderOrigin = and.value[1]?.value as string;

    const status = this.translateService.instant(OrderStatusLabelMap[orderStatus]);

    if (orderOrigin === 'VBC') {
      return this.brandingService.getBusinessAppName(namespace, '').pipe(
        map((businessApp) => {
          if (!businessApp) {
            return this.translateService.instant(OrderOriginLabelMap[orderOrigin]);
          }
          return businessApp;
        }),
        map((businessApp) => {
          let businessAppTextKey = 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.ACTION.WHEN_STATUS_CHANGES';
          if (isWaitUntil) {
            businessAppTextKey = 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.ACTION.WAIT_UNTIL_STATUS_CHANGES';
          }
          return this.translateService.instant(businessAppTextKey, {
            status,
            origin: businessApp,
          });
        }),
      );
    }

    const origin = this.translateService.instant(OrderOriginLabelMap[orderOrigin]);
    let textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.ACTION.WHEN_STATUS_CHANGES';
    if (isWaitUntil) {
      textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.ACTION.WAIT_UNTIL_STATUS_CHANGES';
    }
    return this.translateService.stream(textKey, {
      status,
      origin,
    });
  }

  getActionSubtitleSuccessState(_data: any): Observable<string> {
    return of('');
  }

  getActionSubtitleErrorState(_data: any, _step: WorkflowStepInterface): Observable<string> {
    return of('');
  }

  getActionSubtitleEmptyState(_step: WorkflowStepInterface): Observable<string> {
    return of('');
  }

  getActivityTitle(_namespace: string, _activity: ActivityInterface): Observable<string> {
    return this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.ENTERED_AUTOMATION');
  }

  getActivitySubtitle(namespace: string, activity: ActivityInterface): Observable<string> {
    const triggerData = activity?.triggerData ? JSON.parse(activity.triggerData) : {};

    const status = this.translateService.instant(OrderStatusLabelMap[triggerData.status]);
    const orderOrigin = triggerData.origin;

    const orderId = triggerData.order_id || '';
    const accountGroupId = triggerData.account_group_id || '';
    const orderLink = this.salesOrdersService.getOrderLink(orderId, accountGroupId);

    if (orderOrigin === 'VBC') {
      return this.brandingService.getBusinessAppName(namespace, '').pipe(
        map((businessApp) => {
          if (!businessApp) {
            return this.translateService.instant(OrderOriginLabelMap[orderOrigin]);
          }
          return businessApp;
        }),
        map((businessApp) => {
          return this.translateService.instant(
            'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.ACTIVITY.STATUS_CHANGED',
            {
              status,
              orderLink,
              origin: businessApp,
            },
          );
        }),
      );
    }

    const origin = this.translateService.instant(OrderOriginLabelMap[orderOrigin]);
    return this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.ACTIVITY.STATUS_CHANGED', {
      status,
      origin,
      orderLink,
    });
  }
}
